/* eslint-disable react-native/no-unused-styles */

import React, { type ComponentProps } from 'react';
import { StyleSheet, TouchableOpacity, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';

import { webOnlyStyles } from '../../../../utils';
import { Icon } from '../../../Icon';
import { LoadingDots } from '../../../LoadingDots';

// ─────────────────────────────────────────────────────────────────────────────

export const QuantityStepperToggleButton = (props: Props) => {
  return <QuantityStepperButton iconName="IconPlus2" palette="white" {...props} />;
};

export const QuantityStepperDecreaseButton = (props: Props) => {
  return <QuantityStepperButton iconName="IconMinus2" palette="dark-kale/04" {...props} />;
};

export const QuantityStepperIncreaseButton = (props: Props) => {
  return <QuantityStepperButton iconName="IconPlus2" palette="dark-kale/04" {...props} />;
};

// ─────────────────────────────────────────────────────────────────────────────

const QuantityStepperButton = (props: BaseProps) => {
  const {
    iconName,
    isDisabled,
    isLoading,
    accessibilityLabel,
    palette,
    size = 'md',
    onPress,
  } = props;

  const pressablePaletteStyles = paletteStyles[palette];
  const pressableStyles = [
    styles.pressable,
    size === 'md' ? styles.pressableMD : styles.pressableLG,
    pressablePaletteStyles,
    pressableWebStyles,
    isDisabled ? styles.pressableDisabled : undefined,
  ];

  const iconSize = size === 'md' ? ICON_SIZE_MD : ICON_SIZE_LG;

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={pressableStyles}
      onPress={onPress}
      disabled={isDisabled || isLoading}
      activeOpacity={0.6}
    >
      {isLoading ? (
        <LoadingDots color={theme.colors.DARK_KALE} />
      ) : (
        <Icon name={iconName} width={iconSize} height={iconSize} />
      )}
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

export const BUTTON_SIZE_MD = 54;
export const BUTTON_SIZE_LG = 64;

export const ICON_SIZE_MD = 30;
export const ICON_SIZE_LG = 30;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressableMD: {
    width: BUTTON_SIZE_MD,
    height: BUTTON_SIZE_MD,
    borderRadius: BUTTON_SIZE_MD,
  },
  pressableLG: {
    width: BUTTON_SIZE_LG,
    height: BUTTON_SIZE_LG,
    borderRadius: BUTTON_SIZE_LG,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressableDisabled: {
    opacity: 0.4,
  },
});

const paletteStyles = StyleSheet.create<Record<Palette, ViewStyle>>({
  white: {
    backgroundColor: theme.colors.WHITE,
  },
  'dark-kale/04': {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  'kale/008': {
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Omit<BaseProps, 'iconName' | 'palette'> & {
  palette?: Palette;
};

type BaseProps = {
  iconName: ComponentProps<typeof Icon>['name'];
  onPress: () => void;
  palette: Palette;
  accessibilityLabel?: string;
  size?: 'md' | 'lg';
  isLoading?: boolean;
  isDisabled?: boolean;
};

type Palette = 'white' | 'dark-kale/04' | 'kale/008';
